import { faChromecast } from '@fortawesome/free-brands-svg-icons';
import {
  faCircle,
  faDownload,
  faExternalLinkAlt,
  faTimes,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, colors } from '@mui/material';
import React from 'react';
import T from 'i18n';
import { useSelector } from 'react-redux';
import { selectBundleTabPage, selectCurrentCaseId } from 'common/selectors';
import { OpenOrigin } from './hooks/usePresentView';

type RecentlyPresentedProps = {
  docsToDisplay: Array<any>;
  handleOpenDoc: (doc: any, viewerControlRef: any) => void;
  handleOpenRecentlyPresented: (doc: any, viewerControlRef: any) => void;
  handleRemoveRecentSearch: (e: React.MouseEvent, searchValue: string) => void;
  setSelectedFileForDownload: React.Dispatch<React.SetStateAction<any>>;
  setShowFileVariantsModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowClearAllDocsModal: React.Dispatch<React.SetStateAction<boolean>>;
  recentSearches: { [key: string]: any };
  currentFileMetaData: { id: string; name: string };
  currentFileMetaDataValue: { page: number };
  chipPageNumber: string | null;
  viewerControlRef?: React.RefObject<any>;
};

type GroupedRecentSearchesProps = {
  recentSearches: { [key: string]: any };
  caseId: string | undefined;
  handleOpenRecentlyPresented: (
    doc: any,
    viewerControlRef: any,
    forcePresent?: boolean,
    calledFromHandleOpenDoc?: OpenOrigin,
  ) => void;
  handleRemoveRecentSearch: (e: React.MouseEvent, searchValue: string) => void;
  setSelectedFileForDownload: React.Dispatch<React.SetStateAction<any>>;
  setShowFileVariantsModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowClearAllDocsModal: React.Dispatch<React.SetStateAction<boolean>>;
  currentFileMetaData: { id: string; name: string };
  currentFileMetaDataValue: { page: number };
  viewerControlRef?: React.RefObject<any>;
};

const GroupedRecentSearches = ({
  recentSearches,
  caseId,
  handleOpenRecentlyPresented,
  viewerControlRef,
  handleRemoveRecentSearch,
  setSelectedFileForDownload,
  setShowFileVariantsModal,
  setShowClearAllDocsModal,
  currentFileMetaData,
  currentFileMetaDataValue,
}: GroupedRecentSearchesProps) => {
  const groupedContent = React.useMemo(() => {
    if (Object.keys(recentSearches).length === 0) return null;
    interface DateGroup {
      displayDate: string;
      items: Array<{ searchValue: string; doc: any }>;
    }

    const groupedByDate: { [dateKey: string]: DateGroup } = {};

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    [...Object.entries(recentSearches)]
      .sort(([, docA], [, docB]) => ((docB as any).timestamp || 0) - ((docA as any).timestamp || 0))
      .forEach(([searchValue, doc]: [string, any]) => {
        const date = new Date(doc.timestamp);
        date.setHours(0, 0, 0, 0);
        const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

        if (!groupedByDate[dateKey]) {
          let displayDate;
          if (date.getTime() === today.getTime()) {
            displayDate = 'Today';
          } else if (date.getTime() === yesterday.getTime()) {
            displayDate = 'Yesterday';
          } else {
            displayDate = new Date(doc.timestamp).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            });
          }

          groupedByDate[dateKey] = { displayDate, items: [] };
        }

        groupedByDate[dateKey].items.push({ searchValue, doc });
      });

    return (
      <div className="recent-searches">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '1rem 1.25rem 1rem 0',
          }}
        >
          <span className="section-title">
            {T.translate('case.presentView.recentlyPresented', {
              count: Object.keys(recentSearches)?.length,
            })}
          </span>
          <FontAwesomeIcon
            icon={faTrash}
            onClick={(e) => {
              e.stopPropagation();
              setShowClearAllDocsModal(true);
            }}
            className="clear-btn"
          />
        </div>

        {Object.entries(groupedByDate).map(([dateKey, { displayDate, items }]) => (
          <div key={dateKey} className="date-group">
            <div className="date-header">{displayDate}</div>
            {items.map(({ searchValue, doc }) => {
              let slashCount = 0;
              if (doc?.displayPage?.includes('/')) {
                slashCount = doc?.displayPage?.split('/').length - 1;
              }

              let displayPageMessage = '';
              if (slashCount > 1) {
                displayPageMessage = doc.displayPage;
              } else if (doc.btpParameter) {
                displayPageMessage = doc.displayPage;
              } else {
                displayPageMessage = doc.displayPage === '1' ? '' : `Page: ${doc.displayPage}`;
              }
              let badgeColour: string = colors.grey[200];
              if (currentFileMetaData?.id === doc.id) {
                // if the url contains present=public, then the badge is red, otherwise blue
                if (window.location.search.includes('present=public')) {
                  badgeColour = colors.red[500];
                } else {
                  badgeColour = '#748CEC';
                }
              }
              const docName = doc?.bundleLocations?.[0]?.fileName || doc?.name || '';
              return (
                <div className="recent-search-item" key={searchValue}>
                  <a
                    href={
                      `/case/${caseId}/files/${doc.id}` +
                      `?folderId=${doc.folderId}` +
                      `&goToPage=${doc.localPageNumber ?? 1}` +
                      `&startPage=${doc.startPage}` +
                      `&tab=${doc.tab}` +
                      `&pageCount=${doc.pageCount}` +
                      `&globalPagePrefix=${doc.globalPagePrefix}` +
                      `&view=share`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                    onClick={(e) => {
                      if (doc.private) {
                        e.preventDefault();
                        return;
                      }
                      if (e.ctrlKey || e.metaKey || e.button === 1) {
                        return;
                      }
                      e.preventDefault();
                      if (!doc.private) {
                        handleOpenRecentlyPresented(
                          doc,
                          viewerControlRef,
                          undefined,
                          'recent' as OpenOrigin,
                        );
                      }
                    }}
                  >
                    <div className="search-info">
                      <div className="search-id">
                        <Badge style={{ marginRight: '0.25rem' }} variant="dot">
                          <FontAwesomeIcon icon={faCircle} size="xs" color={badgeColour} />
                        </Badge>
                        {doc.id}
                        {doc.displayPage && (
                          <span className="page-label">{displayPageMessage}</span>
                        )}
                      </div>
                      <div className="search-name">
                        {docName && (docName.length > 25 ? `${doc.name.slice(0, 25)}...` : docName)}
                      </div>
                    </div>
                  </a>
                  <span style={{ gap: 3 }}>
                    <FontAwesomeIcon
                      icon={faDownload}
                      onClick={() => {
                        setSelectedFileForDownload(doc);
                        setShowFileVariantsModal(true);
                      }}
                      className="download-btn"
                      style={{ marginRight: 5 }}
                    />
                    <FontAwesomeIcon
                      icon={faTimes}
                      onClick={(e) => handleRemoveRecentSearch(e, searchValue)}
                      className="remove-btn"
                    />
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  }, [
    recentSearches,
    caseId,
    handleOpenRecentlyPresented,
    handleRemoveRecentSearch,
    setSelectedFileForDownload,
    setShowFileVariantsModal,
    setShowClearAllDocsModal,
    currentFileMetaData,
    currentFileMetaDataValue,
    viewerControlRef,
  ]);

  return groupedContent;
};

const RecentlyPresented = ({
  docsToDisplay,
  handleOpenDoc,
  handleOpenRecentlyPresented,
  handleRemoveRecentSearch,
  setSelectedFileForDownload,
  setShowFileVariantsModal,
  setShowClearAllDocsModal,
  recentSearches,
  currentFileMetaData,
  currentFileMetaDataValue,
  chipPageNumber,
  viewerControlRef,
}: RecentlyPresentedProps) => {
  const caseId = useSelector(selectCurrentCaseId);
  const btpActive = useSelector(selectBundleTabPage);
  return (
    <div style={{ width: '100%', height: '100%', overflowY: 'auto', paddingBottom: 3 }}>
      {docsToDisplay.length > 0 &&
        docsToDisplay.map((doc: any) => {
          const folderId = doc.folderId || doc.bundleLocations?.[0]?.folderId || '';
          const startPage = doc.startPage || doc.bundleLocations?.[0]?.startPage;
          const tab = doc.tab || doc.bundleLocations?.[0]?.tab || '';
          const pageCount = doc.pageCount;
          const globalPagePrefix =
            doc.globalPagePrefix || doc.bundleLocations?.[0]?.globalPagePrefix;
          const docName = doc.bundleLocations?.[0]?.fileName || doc.name || '';
          return (
            <div
              style={{
                border: '0px solid #ccc',
                padding: '0.5rem',
                marginBottom: '1rem',
                borderRadius: 4,
                backgroundColor: '#f9f9f9',
              }}
              key={doc.id}
            >
              <div>{docName}</div>
              <div style={{ fontSize: '0.9rem', color: '#000' }}>
                {doc.id}

                {doc.globalPagePrefix && doc.tab && btpActive ? (
                  <p
                    style={{
                      height: 'auto',
                      flexFlow: 'row',
                    }}
                  >{`${doc.globalPagePrefix?.toUpperCase()} / ${doc.tab} / ${chipPageNumber || 1}`}</p>
                ) : (
                  <p
                    style={{
                      height: 'auto',
                      flexFlow: 'row',
                    }}
                  >{`Page ${chipPageNumber || 1}`}</p>
                )}
              </div>
              <span
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '0.5rem',
                }}
              >
                {doc.private && (
                  <span
                    style={{
                      color: colors.red[500],
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      alignSelf: 'center',
                    }}
                  >
                    {T.translate('generic.private')}
                  </span>
                )}
                <span style={{ alignItems: 'center', display: 'flex' }}>
                  <FontAwesomeIcon
                    icon={faDownload}
                    onClick={() => {
                      if (!doc.private) {
                        setSelectedFileForDownload(doc);
                        setShowFileVariantsModal(true);
                      }
                    }}
                    className="result-download-btn"
                  />
                  <a
                    href={
                      `/case/${caseId}/files/${doc.id}` +
                      `?folderId=${folderId}` +
                      `&goToPage=${chipPageNumber}` +
                      `&startPage=${startPage}` +
                      `&tab=${tab}` +
                      `&pageCount=${pageCount}` +
                      `&globalPagePrefix=${globalPagePrefix}` +
                      `&view=share`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      marginTop: '0.14rem',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faExternalLinkAlt}
                      className="result-download-btn"
                      title={T.translate('case.presentView.tooltip.viewDocument')}
                    />
                  </a>
                </span>
                <FontAwesomeIcon
                  icon={faChromecast}
                  onClick={() => !doc.private && handleOpenDoc(doc, viewerControlRef)}
                  size="2x"
                  style={{
                    cursor: doc.private ? 'not-allowed' : 'pointer',
                    backgroundColor: doc.private ? '#cccccc' : '#748CEC',
                    borderRadius: 4,
                    padding: 4,
                    color: 'white',
                    opacity: doc.private ? 0.6 : 1,
                  }}
                  title={
                    doc.private
                      ? T.translate('case.presentView.tooltip.cannotPresentPrivate')
                      : T.translate('case.presentView.tooltip.presentDocument')
                  }
                />
              </span>
            </div>
          );
        })}
      {Object.keys(recentSearches).length > 0 && (
        <GroupedRecentSearches
          recentSearches={recentSearches}
          caseId={caseId}
          handleOpenRecentlyPresented={handleOpenRecentlyPresented}
          viewerControlRef={viewerControlRef}
          handleRemoveRecentSearch={handleRemoveRecentSearch}
          setSelectedFileForDownload={setSelectedFileForDownload}
          setShowFileVariantsModal={setShowFileVariantsModal}
          setShowClearAllDocsModal={setShowClearAllDocsModal}
          currentFileMetaData={currentFileMetaData}
          currentFileMetaDataValue={currentFileMetaDataValue}
        />
      )}
    </div>
  );
};

export default RecentlyPresented;
